/**
 * Shop Mobile Menu Web Component - Phase 1 Redesign
 * ------------------------------------------------
 * Mobile hamburger menu with flattened category navigation and icon buttons for signed-in users
 *
 * Hierarchy:
 * 1. Shop heading with categories (flat list from all groups)
 * 2. Government heading
 * 3. Categories navigate to submenu if they have subcategories
 * 4. Subcategories work as before (accordion in submenu if nested, otherwise direct link)
 *
 * Attributes:
 * - categories: JSON array of category group objects with nested categories and subcategories
 * - is-signed-in: Boolean attribute for signed-in state
 * - user-name: Full user name
 * - user-email: User email address (masked)
 * - account-url: URL to account page
 * - cash-back-href: URL to cash-back page (default: /cash-back/purchases)
 * - saved-href: URL to saved items page (default: /saved)
 * - government-href: URL to government page
 * - logout-url: URL for logout action
 * - csrf-token: CSRF token for logout form
 * - sign-up-href: URL for sign up
 * - sign-in-href: URL for sign in
 * - open: Boolean attribute to control open/close state
 *
 * Events dispatched:
 * - mobile-menu-open { bubbles, composed }
 * - mobile-menu-close { bubbles, composed }
 * - mobile-menu-submenu-show { detail: { categorySlug }, bubbles, composed }
 * - mobile-menu-back { bubbles, composed }
 */

import { escapeHTML } from "../../../shared/escape_html.js";

const ShopMobileMenu = (function () {
  const TAG_NAME = "shop-mobile-menu";

  /**
   * Safely parses JSON, returning null on failure
   */
  function safeParseJSON(jsonStr) {
    try {
      return JSON.parse(jsonStr);
    } catch {
      return null;
    }
  }

  class ShopMobileMenu extends HTMLElement {
    static get observedAttributes() {
      return [
        "categories",
        "is-signed-in",
        "user-name",
        "user-email",
        "account-url",
        "logout-url",
        "csrf-token",
        "sign-up-href",
        "sign-in-href",
        "government-href",
        "extension-href",
        "app-href",
        "cash-back-href",
        "saved-href",
        "open",
      ];
    }

    constructor() {
      super();
      this._navigationStack = []; // Track navigation history
      this._currentView = "main"; // 'main' or category slug
    }

    connectedCallback() {
      if (!this._initialized) {
        this.render();
        this._fixFragnoButtonSizes();
        this._initialized = true;
        this._attachEventListeners();
      }
    }

    disconnectedCallback() {
      this._detachEventListeners();
    }

    attributeChangedCallback(name, _oldValue, newValue) {
      if (!this._initialized) return;

      if (name === "open") {
        if (newValue !== null) {
          this._openMenu();
        } else {
          this._closeMenu();
        }
      } else if (name === "categories") {
        // Re-render if categories change
        this.render();
        this._fixFragnoButtonSizes();
        this._attachEventListeners();
      }
    }

    _attachEventListeners() {
      // Close buttons (main view + all submenu views)
      if (!this._boundCloseClick) {
        this._boundCloseClick = this._handleCloseClick.bind(this);
      }
      const closeBtns = this.querySelectorAll(".shop-mobile-menu__close-btn");
      closeBtns.forEach((btn) => {
        btn.removeEventListener("click", this._boundCloseClick);
        btn.addEventListener("click", this._boundCloseClick);
      });

      // Icon buttons (wallet, cash back, saved)
      const iconButtons = this.querySelectorAll("[data-icon-button]");
      iconButtons.forEach((btn) => {
        btn.removeEventListener("click", this._onIconButtonClick);
        btn.addEventListener(
          "click",
          (this._onIconButtonClick = this._handleIconButtonClick.bind(this)),
        );
      });

      // Category items with chevrons (navigate to submenu)
      const categoryNavItems = this.querySelectorAll(
        ".shop-mobile-menu__category-nav-item",
      );
      categoryNavItems.forEach((item) => {
        item.removeEventListener("click", this._onCategoryNavClick);
        item.addEventListener(
          "click",
          (this._onCategoryNavClick = this._handleCategoryNavClick.bind(this)),
        );
      });

      // Subcategory toggles (accordion in submenu)
      const subcategoryToggles = this.querySelectorAll(
        ".shop-mobile-menu__subcategory-toggle",
      );
      subcategoryToggles.forEach((toggle) => {
        toggle.removeEventListener("click", this._onSubcategoryToggleClick);
        toggle.addEventListener(
          "click",
          (this._onSubcategoryToggleClick =
            this._handleSubcategoryToggleClick.bind(this)),
        );
      });

      // Back buttons
      const backBtns = this.querySelectorAll(".shop-mobile-menu__back-btn");
      backBtns.forEach((btn) => {
        btn.removeEventListener("click", this._onBackClick);
        btn.addEventListener(
          "click",
          (this._onBackClick = this._handleBackClick.bind(this)),
        );
      });

      // Backdrop
      const backdrop = this.querySelector(".shop-mobile-menu__backdrop");
      if (backdrop) {
        backdrop.removeEventListener("click", this._onBackdropClick);
        backdrop.addEventListener(
          "click",
          (this._onBackdropClick = this._handleCloseClick.bind(this)),
        );
      }

      // Sign up / sign in buttons (no inline onclick — avoids nesting an
      // interpolated URL inside a JS string inside an HTML attribute)
      const signUpBtn = this.querySelector(".shop-mobile-menu__sign-up-btn");
      if (signUpBtn) {
        signUpBtn.removeEventListener("click", this._onSignUpClick);
        signUpBtn.addEventListener(
          "click",
          (this._onSignUpClick = () => this._handleSignUpClick()),
        );
      }

      const signInBtn = this.querySelector(".shop-mobile-menu__sign-in-btn");
      if (signInBtn) {
        signInBtn.removeEventListener("click", this._onSignInClick);
        signInBtn.addEventListener(
          "click",
          (this._onSignInClick = () => this._handleSignInClick()),
        );
      }
    }

    _handleSignUpClick() {
      window.location.href = this.getAttribute("sign-up-href") || "/sign_up";
    }

    _handleSignInClick() {
      window.location.href = this.getAttribute("sign-in-href") || "/sign_in";
    }

    _detachEventListeners() {
      const closeBtn = this.querySelector(".shop-mobile-menu__close-btn");
      if (closeBtn && this._onCloseClick) {
        closeBtn.removeEventListener("click", this._onCloseClick);
      }

      const backdrop = this.querySelector(".shop-mobile-menu__backdrop");
      if (backdrop && this._onBackdropClick) {
        backdrop.removeEventListener("click", this._onBackdropClick);
      }
    }

    _openMenu() {
      const menu = this.querySelector(".shop-mobile-menu__menu");
      const backdrop = this.querySelector(".shop-mobile-menu__backdrop");

      if (menu) {
        menu.removeAttribute("hidden");
      }

      if (backdrop) {
        backdrop.removeAttribute("hidden");
      }

      // Reset to main view when opening
      this._showView("main");

      this.dispatchEvent(
        new CustomEvent("mobile-menu-open", {
          bubbles: true,
          composed: true,
        }),
      );
    }

    _closeMenu() {
      const menu = this.querySelector(".shop-mobile-menu__menu");
      const backdrop = this.querySelector(".shop-mobile-menu__backdrop");

      if (menu) {
        menu.setAttribute("hidden", "");
      }

      if (backdrop) {
        backdrop.setAttribute("hidden", "");
      }

      // Reset navigation when closing
      this._navigationStack = [];
      this._currentView = "main";

      this.dispatchEvent(
        new CustomEvent("mobile-menu-close", {
          bubbles: true,
          composed: true,
        }),
      );
    }

    _fixFragnoButtonSizes() {
      const buttons = this.querySelectorAll("fragno-button");

      buttons.forEach((button) => {
        const buttonElement = button.querySelector("button");

        if (buttonElement) {
          buttonElement.style.setProperty("width", "100%", "important");
        }
      });
    }

    _handleCloseClick() {
      this.removeAttribute("open");
    }

    _handleIconButtonClick(e) {
      const href = e.currentTarget.getAttribute("data-href");
      if (href) {
        window.location.href = href;
      }
    }

    _handleCategoryNavClick(e) {
      const categorySlug = e.currentTarget.getAttribute("data-category-slug");
      if (categorySlug) {
        this._showView(categorySlug);
        this.dispatchEvent(
          new CustomEvent("mobile-menu-submenu-show", {
            bubbles: true,
            composed: true,
            detail: { categorySlug },
          }),
        );
      }
    }

    _handleSubcategoryToggleClick(e) {
      const subcategorySlug = e.currentTarget.getAttribute(
        "data-subcategory-slug",
      );
      const isExpanded =
        e.currentTarget.getAttribute("aria-expanded") === "true";

      // Toggle aria-expanded
      e.currentTarget.setAttribute("aria-expanded", !isExpanded);

      // Get the nested list
      const nestedList = this.querySelector(
        `[data-subcategory-nested="${subcategorySlug}"]`,
      );
      if (!nestedList) return;

      if (!isExpanded) {
        nestedList.removeAttribute("hidden");
      } else {
        nestedList.setAttribute("hidden", "");
      }
    }

    _handleBackClick() {
      if (this._navigationStack.length > 0) {
        const previousView = this._navigationStack.pop();
        this._showView(previousView, false); // Don't add to stack
        this.dispatchEvent(
          new CustomEvent("mobile-menu-back", {
            bubbles: true,
            composed: true,
          }),
        );
      }
    }

    _showView(viewId, addToStack = true) {
      // Hide all views
      const allViews = this.querySelectorAll("[data-view]");
      allViews.forEach((view) => {
        view.setAttribute("hidden", "");
      });

      // Show the requested view
      const targetView = this.querySelector(`[data-view="${viewId}"]`);
      if (targetView) {
        targetView.removeAttribute("hidden");

        // Add current view to stack before switching
        if (addToStack && this._currentView !== viewId) {
          this._navigationStack.push(this._currentView);
        }

        this._currentView = viewId;
      }
    }

    _getCategoryGroups() {
      const categoriesAttr = this.getAttribute("categories");
      if (categoriesAttr) {
        const parsed = safeParseJSON(categoriesAttr);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
      return [];
    }

    _getAllCategories() {
      // Flatten all categories from all groups
      const groups = this._getCategoryGroups();
      const categories = [];
      groups.forEach((group) => {
        if (Array.isArray(group.categories)) {
          categories.push(...group.categories);
        }
      });
      return categories;
    }

    render() {
      const isSignedIn = this.hasAttribute("is-signed-in");
      const userName = this.getAttribute("user-name") || "";
      const userEmail = this.getAttribute("user-email") || "";
      const accountUrl = this.getAttribute("account-url") || "#";
      const logoutUrl = this.getAttribute("logout-url") || "/session";
      const csrfToken = this.getAttribute("csrf-token") || "";
      const signUpHref = this.getAttribute("sign-up-href") || "/sign_up";
      const signInHref = this.getAttribute("sign-in-href") || "/sign_in";
      const governmentHref = this.getAttribute("government-href") || "#";
      const extensionHref =
        this.getAttribute("extension-href") || "/extension/install";
      const appHref = this.getAttribute("app-href") || "/app/install";
      const cashBackHref =
        this.getAttribute("cash-back-href") || "/cash-back/purchases";
      const savedHref = this.getAttribute("saved-href") || "/saved";
      const categoryGroups = this._getCategoryGroups();

      // nosemgrep: insecure-innerhtml -- component render template; every interpolation passes through escapeHTML (text + attribute safe)
      this.innerHTML = `
        <style>
          shop-mobile-menu {
            display: contents;
          }

          /* Mobile Menu Container */
          .shop-mobile-menu__menu {
            position: fixed;
            inset: 0;
            background: white;
            z-index: 1001;
            overflow-y: auto;
            opacity: 1;
            transition: opacity 0.3s ease;
          }

          .shop-mobile-menu__menu[hidden] {
            display: block;
            opacity: 0;
            pointer-events: none;
          }

          /* Backdrop */
          .shop-mobile-menu__backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 1000;
            opacity: 1;
            transition: opacity 0.3s ease;
          }

          .shop-mobile-menu__backdrop[hidden] {
            display: block;
            opacity: 0;
            pointer-events: none;
          }

          /* View Container */
          .shop-mobile-menu__view {
            display: block;
          }

          .shop-mobile-menu__view[hidden] {
            display: none;
          }

          /* Top Section */
          .shop-mobile-menu__top {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding: 0.75rem 1rem;
            gap: 0.75rem;
            min-height: 3.5rem;
          }

          /* User Info Section */
          .shop-mobile-menu__user-info {
            flex: 1;
            min-width: 0;
          }

          .shop-mobile-menu__user-name {
            font-family: var(--font-family-body);
            font-size: 1rem;
            font-weight: var(--font-weight-semibold);
            line-height: 1.5rem;
            color: var(--colors-gray-900);
            margin: 0;
          }

          .shop-mobile-menu__user-email {
            font-family: var(--font-family-body);
            font-size: 0.875rem;
            line-height: 1.25rem;
            color: var(--colors-gray-600);
            margin: 0;
          }

          /* Icon Buttons Row */
          .shop-mobile-menu__icon-buttons {
            display: flex;
            gap: 0.75rem;
            padding: 1rem;
            border-bottom: 1px solid var(--colors-gray-200);
          }

          .shop-mobile-menu__icon-button {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.25rem;
            padding: 0.75rem;
            background: white;
            border: 1px solid var(--colors-gray-400);
            border-radius: var(--radius-3xl);
            cursor: pointer;
            font-family: var(--font-family-body);
            font-size: 1rem;
            font-weight: var(--font-weight-medium);
            line-height: 1.5rem;
            letter-spacing: var(--letter-spacing-text-md);
            color: var(--colors-gray-900);
            text-align: center;
            text-decoration: none;
          }

          .shop-mobile-menu__icon-button:active {
            background: var(--colors-gray-100);
          }

          .shop-mobile-menu__icon-button-icon {
            width: 1.25rem;
            height: 1.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .shop-mobile-menu__icon-button-icon svg {
            width: 100%;
            height: 100%;
          }

          /* Section Headers */
          .shop-mobile-menu__section-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1.25rem 1rem;
            font-family: var(--font-family-display);
            font-size: 1.5rem;
            font-weight: var(--font-weight-medium);
            line-height: 2rem;
            letter-spacing: -0.03em;
            color: var(--colors-gray-900);
            text-decoration: none;
            cursor: pointer;
            background: transparent;
            border: none;
            margin: 0;
          }

          .shop-mobile-menu__circle-chevron {
            width: var(--spacing-4xl);
            height: var(--spacing-4xl);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            border: 1px solid var(--colors-gray-400);
            border-radius: 50%;
            transition: background 0.15s ease, transform 0.1s ease;
          }

          .shop-mobile-menu__circle-chevron:hover {
            background: var(--colors-secondary-hover, #f7f7f7);
          }

          .shop-mobile-menu__circle-chevron:active {
            background: var(--colors-gray-200);
            transform: scale(0.95);
          }

          .shop-mobile-menu__circle-chevron svg {
            width: var(--spacing-lg);
            height: var(--spacing-lg);
          }

          /* Category List */
          .shop-mobile-menu__category-list {
            padding: 0 1rem;
          }

          .shop-mobile-menu__category-item {
            display: block;
          }

          .shop-mobile-menu__category-nav-item {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.875rem 0;
            background: transparent;
            border: none;
            font-family: var(--font-family-body);
            font-size: 0.875rem;
            font-weight: var(--font-weight-medium);
            line-height: 1.25rem;
            letter-spacing: var(--letter-spacing-text-md);
            color: var(--colors-gray-900);
            text-align: left;
            cursor: pointer;
          }

          .shop-mobile-menu__category-nav-item:active {
            color: var(--colors-green-800);
          }

          .shop-mobile-menu__chevron {
            flex-shrink: 0;
            width: 1.25rem;
            height: 1.25rem;
          }

          /* Submenu Header */
          .shop-mobile-menu__submenu-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem 1rem;
          }

          .shop-mobile-menu__back-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem 0.5rem 0.75rem;
            background: var(--colors-gray-100);
            border: none;
            border-radius: var(--radius-full);
            cursor: pointer;
            color: var(--colors-green-800);
            font-family: var(--font-family-body);
            font-size: 1rem;
            font-weight: var(--font-weight-medium-plus);
            line-height: 1.5rem;
            letter-spacing: var(--letter-spacing-text-md);
            flex-shrink: 0;
          }

          .shop-mobile-menu__back-btn:hover {
            background: var(--colors-gray-200);
          }

          .shop-mobile-menu__back-btn svg {
            width: 1rem;
            height: 1rem;
          }

          .shop-mobile-menu__drilldown-title {
            font-family: var(--font-family-body);
            font-size: 1.5rem;
            font-weight: var(--font-weight-semibold);
            line-height: 2rem;
            color: var(--colors-gray-900);
            margin: 0;
            padding: 1.25rem 1rem 0.5rem;
          }

          .shop-mobile-menu__submenu-title {
            font-family: var(--font-family-body);
            font-size: 1.125rem;
            font-weight: var(--font-weight-medium);
            line-height: 1.75rem;
            color: var(--colors-gray-900);
            margin: 0;
          }

          .shop-mobile-menu__close-btn {
            width: 3rem;
            height: 3rem;
            display: flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: none;
            cursor: pointer;
            color: var(--colors-gray-900);
            flex-shrink: 0;
          }

          .shop-mobile-menu__close-btn svg {
            width: 1.5rem;
            height: 1.5rem;
          }

          /* Subcategories in submenu view */
          .shop-mobile-menu__subcategories {
            padding: 0 1rem;
          }

          .shop-mobile-menu__subcategory-item {
            display: block;
          }

          /* Subcategory toggle (accordion in submenu) */
          .shop-mobile-menu__subcategory-toggle {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.875rem 0;
            background: transparent;
            border: none;
            font-family: var(--font-family-body);
            font-size: 1rem;
            font-weight: var(--font-weight-medium);
            line-height: 1.5rem;
            color: var(--colors-gray-900);
            text-align: left;
            cursor: pointer;
          }

          .shop-mobile-menu__subcategory-toggle:active {
            color: var(--colors-green-800);
          }

          .shop-mobile-menu__subcategory-toggle svg {
            transition: transform 0.2s ease;
            flex-shrink: 0;
          }

          .shop-mobile-menu__subcategory-toggle[aria-expanded="true"] svg {
            transform: rotate(180deg);
          }

          /* Nested subcategories (accordion content) */
          .shop-mobile-menu__subcategory-nested {
            padding: 0 0 0.5rem 1rem;
          }

          .shop-mobile-menu__subcategory-link {
            display: block;
            padding: 0.875rem 0;
            font-family: var(--font-family-body);
            font-size: 1rem;
            line-height: 1.5rem;
            color: var(--colors-gray-900);
            text-decoration: none;
          }

          .shop-mobile-menu__subcategory-link:active {
            color: var(--colors-green-800);
          }

          /* Quick Links Section */
          .shop-mobile-menu__quick-links {
            padding: 1.25rem 1rem;
            border-bottom: 1px solid var(--colors-gray-200);
          }

          .shop-mobile-menu__quick-link {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 0.875rem 0;
            text-decoration: none;
            color: var(--colors-gray-900);
          }

          .shop-mobile-menu__quick-link-icon {
            width: 1.25rem;
            height: 1.25rem;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .shop-mobile-menu__quick-link-icon svg {
            width: 100%;
            height: 100%;
            stroke: var(--colors-gray-900);
            fill: none;
          }

          .shop-mobile-menu__quick-link-text {
            font-family: var(--font-family-body);
            font-size: 0.875rem;
            font-weight: var(--font-weight-medium);
            line-height: 1.25rem;
            letter-spacing: var(--letter-spacing-text-md);
            color: var(--colors-gray-900);
          }

          /* Auth Section */
          .shop-mobile-menu__auth-section {
            padding: 2rem 1rem;
          }

          .shop-mobile-menu__auth-text {
            font-family: var(--font-family-display);
            font-size: 1.5rem;
            font-weight: var(--font-weight-regular);
            line-height: 2rem;
            letter-spacing: -0.03em;
            color: var(--colors-gray-900);
            margin: 0 0 1.25rem 0;
          }

          .shop-mobile-menu__auth-buttons {
            display: flex;
            gap: 0.75rem;
          }

          .shop-mobile-menu__auth-buttons fragno-button {
            flex: 1;
          }

          /* Logout Section */
          .shop-mobile-menu__logout-section {
            padding: 2rem 1rem;
          }
        </style>

        <!-- Backdrop -->
        <div class="shop-mobile-menu__backdrop" hidden></div>

        <!-- Mobile Menu -->
        <div class="shop-mobile-menu__menu" hidden>
          <!-- Main Menu View -->
          <div class="shop-mobile-menu__view" data-view="main">
            <!-- Top Section: User Info + Close Button -->
            <div class="shop-mobile-menu__top">
              ${
                isSignedIn
                  ? `
                <div class="shop-mobile-menu__user-info">
                  <p class="shop-mobile-menu__user-name">${escapeHTML(userName)}</p>
                  <p class="shop-mobile-menu__user-email">${escapeHTML(userEmail)}</p>
                </div>
              `
                  : ""
              }
              <button class="shop-mobile-menu__close-btn" type="button" aria-label="Close menu">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            ${
              isSignedIn
                ? `
            <!-- Icon Buttons Row (when signed in) -->
            <div class="shop-mobile-menu__icon-buttons">
              <button class="shop-mobile-menu__icon-button" data-icon-button data-href="${escapeHTML(accountUrl)}" type="button">
                <div class="shop-mobile-menu__icon-button-icon">
                  <svg viewBox="0 0 18.3333 15" fill="none" stroke="#141414" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.5 4.583H0.833M0.833 7.917H3.789c.45 0 .675 0 .893.039.193.034.381.091.561.17.203.088.39.213.765.463l.484.323c.375.25.562.375.765.463.18.078.368.136.561.17.218.039.443.039.893.039h.911c.45 0 .675 0 .893-.039.193-.034.381-.091.561-.17.203-.088.39-.213.765-.462l.484-.324c.375-.25.562-.375.765-.463.18-.078.368-.135.561-.17.218-.038.443-.038.893-.038H17.5M0.833 3.5v8c0 .933 0 1.4.182 1.757.16.313.415.568.729.728.356.182.823.182 1.756.182h11.333c.934 0 1.4 0 1.757-.182.314-.16.569-.415.729-.728.181-.357.181-.824.181-1.757v-8c0-.933 0-1.4-.181-1.757a1.667 1.667 0 0 0-.729-.728C16.233.833 15.767.833 14.833.833H3.5c-.933 0-1.4 0-1.756.182-.314.16-.569.414-.729.728C.833 2.1.833 2.567.833 3.5z"/>
                  </svg>
                </div>
                My Wallet
              </button>
              <button class="shop-mobile-menu__icon-button" data-icon-button data-href="${escapeHTML(cashBackHref)}" type="button">
                <div class="shop-mobile-menu__icon-button-icon">
                  <svg viewBox="0 0 18.3333 18.3333" fill="none" stroke="#141414" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M6.25 11.389a1.944 1.944 0 0 0 1.944 1.944H10a2.083 2.083 0 0 0 0-4.166H8.333a2.083 2.083 0 0 1 0-4.167h1.806a2.083 2.083 0 0 1 2.083 1.944M9.167 3.75V5m0 8.333v1.25M17.5 9.167a8.333 8.333 0 1 1-16.667 0 8.333 8.333 0 0 1 16.667 0z"/>
                  </svg>
                </div>
                My Cash
              </button>
              <button class="shop-mobile-menu__icon-button" data-icon-button data-href="${escapeHTML(savedHref)}" type="button">
                <div class="shop-mobile-menu__icon-button-icon">
                  <svg viewBox="0 0 18.3333 16.6667" fill="none" stroke="#141414" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12.593.833C15.528.833 17.5 3.627 17.5 6.233c0 5.279-8.185 9.6-8.333 9.6S.833 11.512.833 6.233C.833 3.627 2.806.833 5.74.833c1.686 0 2.788.853 3.426 1.603C9.806 1.686 10.907.833 12.593.833z"/>
                  </svg>
                </div>
                Saved
              </button>
            </div>
          `
                : ""
            }

            <!-- Shop Section -->
            <a href="/" class="shop-mobile-menu__section-header">
              <span>Shop</span>
              <span class="shop-mobile-menu__circle-chevron" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="8 4 14 10 8 16"></polyline>
                </svg>
              </span>
            </a>

            <!-- Category Group List (top-level departments) -->
            <div class="shop-mobile-menu__category-list">
              ${categoryGroups
                .map(
                  (group) =>
                    '<div class="shop-mobile-menu__category-item">' +
                    '<button class="shop-mobile-menu__category-nav-item" data-category-slug="' +
                    escapeHTML(group.slug) +
                    '" type="button">' +
                    "<span>" +
                    escapeHTML(group.label) +
                    "</span>" +
                    '<svg class="shop-mobile-menu__chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                    '<polyline points="7 4 13 10 7 16"></polyline>' +
                    "</svg>" +
                    "</button>" +
                    "</div>",
                )
                .join("")}
            </div>

            <!-- Government Section -->
            <a href="${escapeHTML(governmentHref)}" class="shop-mobile-menu__section-header">
              <span>Government</span>
              <span class="shop-mobile-menu__circle-chevron" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="8 4 14 10 8 16"></polyline>
                </svg>
              </span>
            </a>

            <!-- Get the Extension -->
            <a href="${escapeHTML(extensionHref)}" class="shop-mobile-menu__section-header">
              <span>Get the extension</span>
              <span class="shop-mobile-menu__circle-chevron" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </span>
            </a>

            <!-- Get the App -->
            <a href="${escapeHTML(appHref)}" class="shop-mobile-menu__section-header">
              <span>Get the app</span>
              <span class="shop-mobile-menu__circle-chevron" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                  <line x1="12" y1="18" x2="12.01" y2="18"></line>
                </svg>
              </span>
            </a>

            <!-- Quick Links Section -->
            <div class="shop-mobile-menu__quick-links">
              <a href="https://help.id.me" class="shop-mobile-menu__quick-link" target="_blank" rel="noopener noreferrer">
                <span class="shop-mobile-menu__quick-link-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 4H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h3v3l3-3h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path>
                  </svg>
                </span>
                <span class="shop-mobile-menu__quick-link-text">Help Center</span>
              </a>
              <a href="https://www.id.me/careers" class="shop-mobile-menu__quick-link" target="_blank" rel="noopener noreferrer">
                <span class="shop-mobile-menu__quick-link-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="23" y1="11" x2="17" y2="11"></line>
                  </svg>
                </span>
                <span class="shop-mobile-menu__quick-link-text">Join Us</span>
              </a>
              <a href="https://www.id.me/privacy" class="shop-mobile-menu__quick-link" target="_blank" rel="noopener noreferrer">
                <span class="shop-mobile-menu__quick-link-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  </svg>
                </span>
                <span class="shop-mobile-menu__quick-link-text">Privacy</span>
              </a>
            </div>

            ${
              !isSignedIn
                ? `
            <!-- Auth Section (when not signed in) -->
            <div class="shop-mobile-menu__auth-section">
              <p class="shop-mobile-menu__auth-text">Prove it's you.</p>
              <div class="shop-mobile-menu__auth-buttons">
                <fragno-button
                  class="shop-mobile-menu__sign-up-btn"
                  size="md"
                  hierarchy="secondary"
                  label="Create Wallet"
                  width="100%"
                ></fragno-button>
                <fragno-button
                  class="shop-mobile-menu__sign-in-btn"
                  size="md"
                  hierarchy="green-gradient"
                  label="Sign in"
                  width="100%"
                ></fragno-button>
              </div>
            </div>
          `
                : `
            <!-- Logout Button (when signed in) -->
            <div class="shop-mobile-menu__logout-section">
              <form action="${escapeHTML(logoutUrl)}" method="post" style="margin: 0; width: 100%;">
                <input type="hidden" name="_method" value="delete">
                <input type="hidden" name="authenticity_token" value="${escapeHTML(csrfToken)}">
                <fragno-button
                  type="submit"
                  size="md"
                  hierarchy="secondary"
                  label="Log out"
                  style="width: 100%;"
                ></fragno-button>
              </form>
            </div>
          `
            }
          </div>

          <!-- Category Group Views (L1: one for each category group) -->
          ${categoryGroups
            .map(
              (group) => `
              <div class="shop-mobile-menu__view" data-view="${escapeHTML(group.slug)}" hidden>
                <div class="shop-mobile-menu__submenu-header">
                  <button class="shop-mobile-menu__back-btn" type="button" aria-label="Back to Shop">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="13 16 7 10 13 4"></polyline>
                    </svg>
                    Shop
                  </button>
                  <button class="shop-mobile-menu__close-btn" type="button" aria-label="Close menu">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <h2 class="shop-mobile-menu__drilldown-title">${escapeHTML(group.label)}</h2>
                <div class="shop-mobile-menu__subcategories">
                  ${(group.categories || [])
                    .map((category) => {
                      const hasSubcategories =
                        Array.isArray(category.subcategories) &&
                        category.subcategories.length > 0;
                      if (hasSubcategories) {
                        return `
                        <div class="shop-mobile-menu__subcategory-item">
                          <button
                            class="shop-mobile-menu__category-nav-item"
                            data-category-slug="${escapeHTML(category.slug)}"
                            type="button">
                            <span>${escapeHTML(category.label)}</span>
                            <svg class="shop-mobile-menu__chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <polyline points="7 4 13 10 7 16"></polyline>
                            </svg>
                          </button>
                        </div>
                      `;
                      } else {
                        return `
                        <div class="shop-mobile-menu__subcategory-item">
                          <a href="/categories/${escapeHTML(category.slug)}" class="shop-mobile-menu__subcategory-link">
                            ${escapeHTML(category.label)}
                          </a>
                        </div>
                      `;
                      }
                    })
                    .join("")}
                </div>
              </div>
            `,
            )
            .join("")}

          <!-- Category Submenu Views (L2: one for each category with subcategories) -->
          ${categoryGroups
            .flatMap((group) =>
              (group.categories || [])
                .filter(
                  (cat) =>
                    Array.isArray(cat.subcategories) &&
                    cat.subcategories.length > 0,
                )
                .map(
                  (category) => `
              <div class="shop-mobile-menu__view" data-view="${escapeHTML(category.slug)}" hidden>
                <!-- Submenu Header with Back Button -->
                <div class="shop-mobile-menu__submenu-header">
                  <button class="shop-mobile-menu__back-btn" type="button" aria-label="Back to ${escapeHTML(group.label)}">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="13 16 7 10 13 4"></polyline>
                    </svg>
                    ${escapeHTML(group.label)}
                  </button>
                  <button class="shop-mobile-menu__close-btn" type="button" aria-label="Close menu">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <h2 class="shop-mobile-menu__drilldown-title">${escapeHTML(category.label)}</h2>

                <!-- Subcategories -->
                <div class="shop-mobile-menu__subcategories">
                  <div class="shop-mobile-menu__subcategory-item">
                    <a href="/categories/${escapeHTML(category.slug)}" class="shop-mobile-menu__subcategory-link">
                      Shop all
                    </a>
                  </div>
                  ${category.subcategories
                    .map((subcategory) => {
                      const hasNested =
                        Array.isArray(subcategory.subcategories) &&
                        subcategory.subcategories.length > 0;

                      if (hasNested) {
                        // Check if nested items have their own children
                        const hasDeepNesting = subcategory.subcategories.some(
                          (nested) =>
                            Array.isArray(nested.subcategories) &&
                            nested.subcategories.length > 0,
                        );

                        if (hasDeepNesting) {
                          // Navigate to another submenu view
                          return `
                          <div class="shop-mobile-menu__subcategory-item">
                            <button
                              class="shop-mobile-menu__category-nav-item"
                              data-category-slug="${escapeHTML(subcategory.slug)}"
                              type="button">
                              <span>${escapeHTML(subcategory.label)}</span>
                              <svg class="shop-mobile-menu__chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="7 4 13 10 7 16"></polyline>
                              </svg>
                            </button>
                          </div>
                        `;
                        } else {
                          // Accordion for leaf nodes
                          return `
                          <div class="shop-mobile-menu__subcategory-item">
                            <button
                              class="shop-mobile-menu__subcategory-toggle"
                              data-subcategory-slug="${escapeHTML(subcategory.slug)}"
                              aria-expanded="false"
                              type="button">
                              <span>${escapeHTML(subcategory.label)}</span>
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="6 8 10 12 14 8"></polyline>
                              </svg>
                            </button>
                            <div class="shop-mobile-menu__subcategory-nested" data-subcategory-nested="${escapeHTML(subcategory.slug)}" hidden>
                              ${subcategory.subcategories
                                .map(
                                  (nested) => `
                                <a href="/categories/${escapeHTML(nested.slug)}" class="shop-mobile-menu__subcategory-link">
                                  ${escapeHTML(nested.label)}
                                </a>
                              `,
                                )
                                .join("")}
                            </div>
                          </div>
                        `;
                        }
                      } else {
                        // No nested items: direct link
                        return `
                        <div class="shop-mobile-menu__subcategory-item">
                          <a href="/categories/${escapeHTML(subcategory.slug)}" class="shop-mobile-menu__subcategory-link">
                            ${escapeHTML(subcategory.label)}
                          </a>
                        </div>
                      `;
                      }
                    })
                    .join("")}
                </div>
              </div>

              <!-- Deep submenu views -->
              ${category.subcategories
                .filter(
                  (sub) =>
                    Array.isArray(sub.subcategories) &&
                    sub.subcategories.some(
                      (nested) =>
                        Array.isArray(nested.subcategories) &&
                        nested.subcategories.length > 0,
                    ),
                )
                .map(
                  (subcategory) => `
                <div class="shop-mobile-menu__view" data-view="${escapeHTML(subcategory.slug)}" hidden>
                  <div class="shop-mobile-menu__submenu-header">
                    <button class="shop-mobile-menu__back-btn" type="button" aria-label="Back to ${escapeHTML(category.label)}">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="13 16 7 10 13 4"></polyline>
                      </svg>
                      ${escapeHTML(category.label)}
                    </button>
                    <button class="shop-mobile-menu__close-btn" type="button" aria-label="Close menu">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                  <h2 class="shop-mobile-menu__drilldown-title">${escapeHTML(subcategory.label)}</h2>
                  <div class="shop-mobile-menu__subcategories">
                    ${subcategory.subcategories
                      .map(
                        (nested) => `
                      <div class="shop-mobile-menu__subcategory-item">
                        <a href="/categories/${escapeHTML(nested.slug)}" class="shop-mobile-menu__subcategory-link">
                          ${escapeHTML(nested.label)}
                        </a>
                      </div>
                    `,
                      )
                      .join("")}
                  </div>
                </div>
              `,
                )
                .join("")}
            `,
                ),
            )
            .join("")}
        </div>
      `;
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopMobileMenu);
  }

  return ShopMobileMenu;
})();

export { ShopMobileMenu };
